import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';

const ClientSelectionContext = createContext();

export const ClientSelectionProvider = ({ children }) => {
  // No card click state for clients (no onClick cards)
  // Filters state - matching MyClients component filters
  const [filters, setFilters] = useState({
    clientName: "",
    company: "",
    email: "",
    gstin: ""
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 5,
    hasMore: true,
    totalClients: 0
  });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Store filtered clients - simpler since no card-based filtering
  const [filteredClients, setFilteredClients] = useState([]);
  
  const lastUpdateTimeRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const triggerClientRefresh = useCallback(() => {
    setRefreshKey(prevKey => prevKey + 1);
  }, []);

  const setFilteredClientsStable = useCallback((clients) => {
    const now = Date.now();
    
    // Throttling to prevent excessive updates
    if (now - lastUpdateTimeRef.current < 50) {
      console.log('Throttling setFilteredClients update');
      return;
    }
    lastUpdateTimeRef.current = now;
    
    console.log('Client Context setFilteredClients called with:', clients?.length || 0, 'clients');
    
    setFilteredClients(prev => {
      // Always update if clients is empty array (important for clearing)
      if (Array.isArray(clients) && clients.length === 0) {
        console.log('Setting empty clients');
        return [];
      }
      
      // Check if clients are actually different
      if (prev === clients) {
        console.log('Clients reference is same, no update needed');
        return prev;
      }
      
      if (prev && prev.length === clients.length) {
        let hasChanged = false;
        for (let i = 0; i < clients.length; i++) {
          if (prev[i] !== clients[i]) {
            hasChanged = true;
            break;
          }
        }
        if (!hasChanged) {
          console.log('Clients content is same, no update needed');
          return prev;
        }
      }
      
      console.log('Updating filtered clients with', clients.length, 'clients');
      return Array.isArray(clients) ? [...clients] : [];
    });
  }, []);
  
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    // Pagination
    params.set('page', pagination.page.toString());
    params.set('limit', pagination.limit.toString());
    
    // Search term
    if (searchTerm && searchTerm.trim()) {
      params.set('search', searchTerm.trim());
    }
    
    // Apply all filters with correct parameter names for client API
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.trim()) {
        // Map context filter names to API parameter names if needed
        const apiKey = key === 'clientName' ? 'name' : key;
        params.set(apiKey, value.trim());
      }
    });
    
    console.log('Client params:', params.toString());
    return params.toString();
  }, [pagination, searchTerm, filters]);
  
  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      page: 0,
      hasMore: true
    }));
  }, []);
  
// Clear all filters and reset state
const clearAllFilters = useCallback(() => {
  console.log('Clearing all client filters and resetting state');
  
  setFilters({
    clientName: "",
    company: "",
    email: "",
    gstin: ""
  });
  
  setSearchTerm('');
  resetPagination();
  setFilteredClients([]);
  
  // Also clear the search input field in DetailSection
  const searchInput = document.querySelector('.detailsection-search-input');
  if (searchInput) {
    searchInput.value = '';
    // Trigger the onChange event to update DetailSection's searchInput state
    const event = new Event('input', { bubbles: true });
    searchInput.dispatchEvent(event);
  }
}, [resetPagination]);
  // Stable setters
  const setFiltersStable = useCallback((newFilters) => {
    setFilters(prevFilters => {
      const hasChanged = Object.keys(newFilters).some(key => 
        newFilters[key] !== prevFilters[key]
      );
      
      if (hasChanged) {
        console.log('Client filters changed:', newFilters);
        resetPagination();
        return newFilters;
      }
      
      return prevFilters;
    });
  }, [resetPagination]);
  
  const setSearchTermStable = useCallback((term) => {
    if (term !== searchTerm) {
      console.log('Client search term changed:', term);
      setSearchTerm(term);
      resetPagination();
    }
  }, [searchTerm, resetPagination]);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const hasSearch = searchTerm.trim() !== '';
    const hasFilters = Object.values(filters).some(value => value && value.trim() !== '');
    
    console.log('Client hasActiveFilters check:', {
      hasSearch, 
      hasFilters,
      searchTerm,
      filters
    });
    
    return hasSearch || hasFilters;
  }, [searchTerm, filters]);
  
  // Update pagination info
  const updatePaginationInfo = useCallback((totalClients, hasMore) => {
    setPagination(prev => ({
      ...prev,
      totalClients,
      hasMore
    }));
  }, []);
  
  // Load more clients (for pagination)
  const loadMore = useCallback(() => {
    if (pagination.hasMore) {
      setPagination(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  }, [pagination.hasMore]);
  
  const contextValue = useMemo(() => ({
    // Filters
    filters,
    setFilters: setFiltersStable,
    
    // Search
    searchTerm,
    setSearchTerm: setSearchTermStable,
    
    // Pagination
    pagination,
    setPagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    
    // Filtered clients
    filteredClients,
    setFilteredClients: setFilteredClientsStable,
    refreshKey,
    triggerClientRefresh,
    
    // Utilities
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
  }), [
    filters,
    setFiltersStable,
    searchTerm,
    setSearchTermStable,
    pagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    filteredClients,
    setFilteredClientsStable,
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    refreshKey,
    triggerClientRefresh
  ]);
  
  return (
    <ClientSelectionContext.Provider value={contextValue}>
      {children}
    </ClientSelectionContext.Provider>
  );
};

export const useClientSelection = () => {
  const context = useContext(ClientSelectionContext);
  if (!context) {
    throw new Error('useClientSelection must be used within an ClientSelectionProvider');
  }
  return context;
};